import { useEffect, useState } from 'react'
import Template from './../Template'
import * as SG from '../../style/Styles'
import * as S from '../../style/User'
import * as TXT from './../../util/Texts'
import { useParams, useNavigate } from 'react-router-dom'
import * as GroupService from './../../services/Group.service'
import * as UserService from './../../services/User.service'
import { ERROR_ALERT, SUCCESS_ALERT } from './../../util/AlertsMsg'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { StyledTableCell, StyledTableRow } from './../../components/table/Style'

export default function GroupForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  const isEdit = id !== undefined
  const currentUserId = parseInt(localStorage.getItem('us_id') || '0')
  
  const [description, setDescription] = useState<string>('')
  const [members, setMembers] = useState<any[]>([]) // Membros locais do grupo
  const [isOwner, setIsOwner] = useState<boolean>(true) // Por padrão, se for criação, ele é o dono
  const [loading, setLoading] = useState<boolean>(false)
  
  // Estados para busca de usuários para adicionar
  const [searchName, setSearchName] = useState<string>('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searchLoading, setSearchLoading] = useState<boolean>(false)

  const fetchGroupDetails = async () => {
    if (!id) return
    setLoading(true)
    const result = await GroupService.GetGroup(id)
    if (result.data && result.data.status) {
      const g = result.data.group
      setDescription(g.description)
      setMembers(g.users || [])
      setIsOwner(g.user_id === currentUserId)
    } else {
      ERROR_ALERT(TXT.COULD_NOT_LOAD_GROUP_INFO)
      navigate('/groups')
    }
    setLoading(false)
  }

  const handleSearchUsers = async () => {
    if (searchName.trim().length < 2) {
      ERROR_ALERT(TXT.MIN_SEARCH_CHARS)
      return
    }
    setSearchLoading(true)
    try {
      const result = await UserService.GetAllUsers({ name: searchName })
      if (result.data && result.data.users) {
        // Filtrar para não exibir quem já faz parte do grupo
        const filtered = result.data.users.filter(
          (u: any) => !members.some((m) => parseInt(m.id) === parseInt(u.id))
        )
        setSearchResults(filtered)
      }
    } catch (err) {
      console.warn(err)
      ERROR_ALERT(TXT.WRONG_TRY_AGAIN)
    }
    setSearchLoading(false)
  }

  const handleAddMember = (user: any) => {
    // Adiciona o usuário na lista local de integrantes do grupo
    setMembers((prev) => [...prev, user])
    // Remove dos resultados de pesquisa
    setSearchResults((prev) => prev.filter((u) => u.id !== user.id))
  }

  const handleRemoveMember = (userId: any) => {
    // O criador do grupo não pode remover a si próprio!
    if (parseInt(userId) === currentUserId) {
      ERROR_ALERT(TXT.CANT_REMOVE_YOURSELF)
      return
    }
    setMembers((prev) => prev.filter((m) => m.id !== userId))
  }

  const handleSave = async () => {
    if (description.trim() === '') {
      ERROR_ALERT(TXT.GROUP_NAME_REQUIRED)
      return
    }

    const memberIds = members.map((m) => m.id)
    
    // Garantir que o criador sempre esteja na lista
    if (!memberIds.includes(currentUserId)) {
      memberIds.push(currentUserId)
    }

    const payload = {
      description: description,
      members: memberIds
    }

    let result
    if (isEdit) {
      result = await GroupService.UpdateGroup(id, payload)
    } else {
      result = await GroupService.CreateGroup(payload)
    }

    if (result.data && result.data.status) {
      SUCCESS_ALERT(result.data.message)
      setTimeout(() => {
        navigate('/groups')
      }, 1500)
    } else {
      try {
        ERROR_ALERT(result.response.data.message)
      } catch (err) {
        console.warn(err)
        ERROR_ALERT(TXT.WRONG_TRY_AGAIN)
      }
    }
  }

  useEffect(() => {
    if (isEdit) {
      fetchGroupDetails()
    } else {
      // Se for criação, obter dados do próprio criador para pré-adicioná-lo na lista de membros local
      const currentUserName = localStorage.getItem('us_nm') || ''
      const currentUserReg = localStorage.getItem('us_re') || ''
      setMembers([{
        id: currentUserId,
        name: currentUserName,
        register: currentUserReg,
        cs: localStorage.getItem('us_cs') || '-',
        department: localStorage.getItem('us_dept') || '-'
      }])
    }
  }, [id])

  return (
    <Template>
      <SG.PADD style={{ paddingBottom: 40 }}>
        <S.BoxFormInteral className='formGroup' style={{ maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
          <SG.Title style={{ textAlign: 'center', marginBottom: 30 }}>
            {isEdit ? (isOwner ? TXT.EDIT_GROUP : TXT.VIEW_GROUP) : TXT.NEW_GROUP}
          </SG.Title>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <SG.IMG src='/img/loading.gif' alt={TXT.LOADING} style={{ width: 50 }} />
              <p>{TXT.LOADING_GROUP_INFO}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
              
              {/* Nome do grupo */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 'bold', color: '#1976d2' }}>{TXT.GROUP_NAME}</span>
                <SG.InputInternal 
                  name='description'
                  className='name-group-in'
                  value={description}
                  placeholder={TXT.GROUP_NAME_PLACEHOLDER}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!isOwner}
                  style={{ marginTop: 0 }}
                />
              </div>

              <div 
                className={isOwner ? 'groupFormGrid' : ''}
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isOwner ? undefined : '1fr', 
                  gap: 30 
                }}
              >
                
                {/* Coluna de Adicionar Integrantes (apenas proprietário) */}
                {isOwner && (
                  <div style={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 12, 
                    padding: 20, 
                    backgroundColor: '#fafafa',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 15,
                    maxWidth: '100%',
                    minWidth: 0
                  }}>
                    <h3 style={{ margin: 0, color: '#333', fontSize: 16 }}>{TXT.ADD_MEMBERS}</h3>
                    
                    <div className="groupSearchRow">
                      <SG.InputInternal 
                        name='searchName'
                        value={searchName}
                        placeholder={TXT.MEMBER_NAME_PLACEHOLDER}
                        onChange={(e) => setSearchName(e.target.value)}
                        style={{ marginTop: 0 }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSearchUsers()
                        }}
                      />
                      <SG.PrimaryButtom 
                        onClick={handleSearchUsers}
                        style={{ width: 'auto', padding: '0 20px', whiteSpace: 'nowrap' }}
                      >
                        {searchLoading ? TXT.SEARCHING : TXT.SEARCH}
                      </SG.PrimaryButtom>
                    </div>

                    {/* Resultados da busca */}
                    <div style={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 10 
                    }}>
                      {searchResults.length > 0 ? (
                        searchResults.map((user) => (
                          <div 
                            key={user.id} 
                            style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center', 
                              padding: '10px 15px', 
                              backgroundColor: '#fff', 
                              border: '1px solid #eee', 
                              borderRadius: 8 
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 'bold', fontSize: 14 }}>{user.name}</div>
                              <div style={{ fontSize: 12, color: '#666' }}>
                                {`${TXT.REGISTER}: ${user.register} | ${TXT.PLANT}: ${user.cs || '-'}`}
                              </div>
                            </div>
                            <SG.PrimaryButtom 
                              onClick={() => handleAddMember(user)}
                              style={{ width: 'auto', padding: '6px 12px', fontSize: 12, backgroundColor: '#2e7d32' }}
                            >
                              {TXT.ADD}
                            </SG.PrimaryButtom>
                          </div>
                        ))
                      ) : (
                        searchName && !searchLoading && (
                          <div style={{ textAlign: 'center', color: '#888', padding: '10px 0', fontSize: 13 }}>
                            {TXT.NO_PARTICIPANT_FOUND}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Coluna da Lista atual de Integrantes */}
                <div style={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 12, 
                  padding: 20, 
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 15,
                  maxWidth: '100%',
                  minWidth: 0
                }}>
                  <h3 style={{ margin: 0, color: '#333', fontSize: 16 }}>
                    {`${TXT.MEMBERS} (${members.length})`}
                  </h3>

                  <TableContainer component={Paper} style={{ maxHeight: '400px', overflowX: 'auto', width: '100%' }}>
                    <Table size="small" aria-label="members table" style={{ minWidth: 500 }}>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell className='userTh'>{TXT.MEMBER}</StyledTableCell>
                          <StyledTableCell className='userTh'>{TXT.MATRICULA_UPPER}</StyledTableCell>
                          <StyledTableCell className='userTh'>{TXT.PLANT_UPPER}</StyledTableCell>
                          {isOwner && <StyledTableCell className='userTh' style={{ width: '60px' }}></StyledTableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {members.length > 0 ? (
                          members.map((row) => (
                            <StyledTableRow key={row.id}>
                              <StyledTableCell className='txtTd' style={{ fontWeight: parseInt(row.id) === currentUserId ? 'bold' : 'normal' }}>
                                {row.name} {parseInt(row.id) === currentUserId && TXT.YOU}
                              </StyledTableCell>
                              <StyledTableCell className='txtTd'>{row.register}</StyledTableCell>
                              <StyledTableCell className='txtTd'>{row.cs || '-'}</StyledTableCell>
                              {isOwner && (
                                <StyledTableCell align="center">
                                  {parseInt(row.id) !== currentUserId && (
                                    <SG.Span 
                                      title={TXT.REMOVE_MEMBER} 
                                      onClick={() => handleRemoveMember(row.id)} 
                                      style={{ cursor: 'pointer', color: '#d32f2f', fontSize: 16 }}
                                    >
                                      <i className="fa-solid fa-trash-can"></i>
                                    </SG.Span>
                                  )}
                                </StyledTableCell>
                              )}
                            </StyledTableRow>
                          ))
                        ) : (
                          <StyledTableRow>
                            <StyledTableCell colSpan={isOwner ? 4 : 3} style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                              {TXT.NO_MEMBERS_ADDED}
                            </StyledTableCell>
                          </StyledTableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>

              </div>

              {/* Botões de Ação */}
              <SG.Div className='boxbutton' style={{ display: 'flex', justifyContent: 'center', gap: 15, marginTop: 10 }}>
                <SG.PrimaryButtom 
                  onClick={() => navigate('/groups')} 
                  style={{ backgroundColor: '#757575', color: '#fff', width: 'auto', padding: '10px 25px' }}
                >
                  <i className="fa-solid fa-arrow-left"></i> {TXT.GO_BACK}
                </SG.PrimaryButtom>
                
                {isOwner && (
                  <SG.PrimaryButtom 
                    onClick={handleSave}
                    style={{ width: 'auto', padding: '10px 25px' }}
                  >
                    <i className="fa-regular fa-floppy-disk"></i> {TXT.SAVE}
                  </SG.PrimaryButtom>
                )}
              </SG.Div>

            </div>
          )}

        </S.BoxFormInteral>
      </SG.PADD>
    </Template>
  )
}
